import { authClient, getServiceURL } from '@/utils'
import { formatYYMMDD } from '@/utils/formatDate'
import { productConstants } from '@/constants'
import Toggles from '@domain/toggles/Toggles'
import TogglesV2 from '@domain/toggles/TogglesManager'
import DataProvider from '@domain/application/DataProvider'
import DataSource from '@domain/application/DataSourceAPIImplementation'
import LuminariesFactory from '@domain/factory/Luminaries'
import ProjectConverter from '@domain/converters/ProjectConverter'
import ArticleCollection from '@domain/catalog/ArticleCollection'
import Space from '@domain/composition/Space'
import ProjectApi from '@api/ProjectApi'
import ArticleCollectionApi from '@api/ArticleCollectionApi'
import { getServiceURLserverSide } from '@domain/infrastructure'


const getProject = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('projects')}/v1/projects/${projectId}`, {
		params: {
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const getProjectv2 = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}`, {
		params: {
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const getProjectV3 = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/project/${projectId}`, {
		params: {
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}
// }

/**
 * Lectura del detalle de proyecto para las pantallas que no pasan por
 * `projectActions.getProject` (y por tanto no leen `getProjectDetailsByV3`).
 * El cuerpo de v3 es el mismo que el de v2, así que el llamante no cambia.
 */
const getProjectDetail = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return Toggles.isEnabled('fixProjectV3insteadV2')
		? getProjectV3({ projectId, lang, withPrices, isProjectPage })
		: getProjectv2({ projectId, lang, withPrices, isProjectPage })
}

const getCrmOnlyProducts = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/project/${projectId}/crm-only-products`)
}

const getProjectToBudgetV2 = ({ projectId, lang, withPrices = false, isProjectPage = false }) => {
	return authClient.get(`${getServiceURL('apiv2')}/project-to-budget`, {
		params: {
			projectId,
			lang,
			prices: withPrices,
			parseProducts: isProjectPage
		}
	})
}

const createProject = ({ catalogId, products, name }) => {
	return authClient.post(`${getServiceURL('projects')}/v1/projects`, {
		name,
		catalogId,
		products
	})
}

const createProjectv2 = (body) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, body)
}

const upsertVersionV2 = (projectId) => {
	return authClient.post(`${getServiceURL('apiv2')}/project/${projectId}/version/update`)
}

const setProjectNameV2 = (projectId, projectName) => {
	return authClient.put(`${getServiceURL('apiv2')}/project/${projectId}/projectName`, { projectName: projectName })
}

const duplicateProject = (projectId) => {
	if (Toggles.isEnabled('duplicateProjectsWithV3')) {
		return authClient.post(`${getServiceURLserverSide('v3')}/projects/${projectId}/clone`)
	} else {
		return authClient.post(`${getServiceURL('projects')}/v1/projects/${projectId}/clone`)
	}
}

const versionProject = (projectId, email) => {
	const queryParams = email ? `?email=${email}` : '';
	if (Toggles.isEnabled('versionateByV3') && !email) {
		return authClient.post(`${getServiceURLserverSide('v3')}/project/${projectId}/versionate`)
	} else {
		return authClient.post(`${getServiceURL('apiv2')}/project/${projectId}/version${queryParams}`)
	}
}

const getProjectVersions = (projectId) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}/versions`)
}

const createProjectWithReferences = ({ catalogId, collection, referencesDetails, projectName }) => {
	return authClient.post(`${getServiceURL('projects')}/v1/projects`, {
		name: projectName,
		catalogId,
		products: referencesDetails.map((referenceDetails) => ({
			type: referenceDetails.type || 'REGULAR',
			familyId: referenceDetails.familyId,
			model: referenceDetails.model,
			quantity: referenceDetails.quantity,
			sublevel: collection.sublevel,
			subFamily: collection.subFamily,
			applicationLocation: {
				id: collection.applicationLocation.id
			},
			...(referenceDetails.configuratorType && { configuratorType: referenceDetails.configuratorType })
		}))
	})
}

const addProduct = ({ projectId, product }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		[{ quantity: 1, ...product }]
	)
}

const addProducts = ({ projectId, products }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		products.map((product) => ({ quantity: 1, ...product }))
	)
}

const addProductsV2 = ({ projectId, products, config }) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, {
		projectId,
		products,
		config
	})
}

const getArticleCollection = async ({ catalog, applicationLocation, subfamily }) => {
	const response = await ArticleCollectionApi.getModels(catalog, applicationLocation, subfamily, 'without') || []
	if (response.message) {
		return
	}
	const models = response.find(elem => elem.catalogId == catalog)?.models || []
	const modelIds = models.map(model => model.model).sort((a, b) => a - b)

	const variations = await ArticleCollectionApi.getArticleCollection(modelIds, catalog)
	if (!variations) return

	return ArticleCollection.getInstance(variations)
}

const addProductByRef = async ({ projectId, reference, quantity = 1 }) => {
	if (Toggles.isEnabled('addProductByReferenceV3')) {
		return authClient.post(
			`${getServiceURLserverSide('v3')}/projects/${projectId}/products/byReference`,
			{ reference, quantity }
		)
	}
	if (Toggles.isEnabled('refactorAddProductByReference')) {
		return addProductByRefV2({ projectId, reference })
	}
	return addProductByRefV1({ projectId, reference })
}

const addProductByRefV1 = async ({ projectId, reference }) => {
	const lightData = await DataProvider.getInformationByReference(new DataSource(), reference)

	const luminary = LuminariesFactory.createLuminary(lightData)

	const light = Array.isArray(luminary.getLights())
		? luminary.getLights()[0].element
		: luminary.getLights().element
	light.setAttributesByReference(reference)

	const applicationLocation = light.getApplicationLocation()[0].id
	const subfamily = light.getSubFamily()
	const catalog = reference.includes('-') ? 21 : 16

	let space = new Space([], null, null, null, null, null, null, 'ATTRIBUTES_CONFIGURATOR', 'PRIVATE_AREA')
	space = space.addElement({ element: luminary, options: { position: [0, 0, 0] } }, space.getId())
	const articleCollection = await getArticleCollection({ catalog, applicationLocation, subfamily })
	const BOM = ProjectConverter.convertSpaceToBOM(space, articleCollection)

	return await ProjectApi.addToProject(space, catalog, BOM, projectId)
}

const addProductByRefV2 = async ({ projectId, reference }) => {
	const lightData = await DataProvider.getInformationByReference(new DataSource(), reference)

	const { subfamily, catalog, model } = lightData
	const luminary = LuminariesFactory.createLuminary(lightData)

	let space = new Space([], null, null, null, null, null, null, 'ATTRIBUTES_CONFIGURATOR', 'PRIVATE_AREA')
	space = space.addElement({ element: luminary, options: { position: [0, 0, 0] } }, space.getId())

	const variations = await ArticleCollectionApi.getArticleCollection([model], catalog, subfamily)
	const articleCollectionInstance = ArticleCollection.getInstance(variations)
	const BOM = ProjectConverter.convertSpaceToBOM(space, articleCollectionInstance)
	return await ProjectApi.addToProject(space, catalog, BOM, projectId)
}


const addProductsByRef = ({ projectId, collection, referencesDetails }) => {
	return authClient.post(
		`${getServiceURL('projects')}/v1/projects/${projectId}/products`,
		referencesDetails.map((referenceDetails) => ({
			type: referenceDetails.type || 'REGULAR',
			familyId: referenceDetails.familyId,
			model: referenceDetails.model,
			quantity: referenceDetails.quantity || 1,
			sublevel: collection.sublevel,
			subFamily: collection.subFamily,
			applicationLocation: {
				id: collection.applicationLocation.id
			},
			...(referenceDetails.configuratorType && { configuratorType: referenceDetails.configuratorType })
		}))
	)
}

const createBudget = ({ budgetDetails, formData }) => {
	const body = {
		projectId: budgetDetails.id,
		projectName: budgetDetails.name,
		budgetNumber: formData.budgetNumber,
		clientName: formData.clientName,
		createdBy: formData.createdBy,
		creationDate: formatYYMMDD(formData.creationDate),
		validationDate: formatYYMMDD(formData.validationDate),
		currency: budgetDetails.currency,
		discount: budgetDetails?.discount || 0,
		surcharge: budgetDetails.surcharge || 0,
		iva: budgetDetails.iva || 0,
		discountValue: budgetDetails?.discountValue || 0,
		surchargeValue: budgetDetails.surchargeValue || 0,
		ivaValue: budgetDetails.ivaValue || 0,
		subTotalNet: budgetDetails.subTotal || 0,
		totalNet: budgetDetails.netTotal || 0,
		comments: budgetDetails.comment || '',
		products: Object.values(budgetDetails.products.byId).reduce((acc, product) => {
			if (product.quantity <= 0) {
				return acc
			}

			if (product?.type === productConstants.PRODUCT_TYPE_CREATION) {
				return [
					...acc,
					{
						...product,
						discount: product?.discount || 0,
						pvp: product?.quantity * product?.prices.unitGross,
						netPrice: product?.discount
							? product?.quantity * product?.prices?.unitGross * (1 - product.discount / 100)
							: product?.quantity * product?.prices?.unitGross,
						discountValue: product?.discount ? product?.prices?.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices?.unitGross,
						compositions: product.compositions.reduce((acc, composition) => {
							return [
								...acc,
								{
									...composition,
									pvp: product?.quantity * composition?.prices?.unitGross,
									netPrice: product?.discount
										? product?.quantity * composition?.prices?.unitGross * (1 - product.discount / 100)
										: product?.quantity * composition?.prices?.unitGross,
									...(composition.accessories && {
										accessories: composition.accessories.reduce((acc, accessory) => {
											return [
												...acc,
												{
													...accessory,
													netPrice: accessory?.prices?.totalNet,
													pvp: accessory?.quantity * accessory?.prices?.unitGross,
													unitPvp: accessory?.prices?.unitGross
												}
											]
										}, [])
									}),
									articles: composition.articles.reduce((acc, article) => {
										return [
											...acc,
											{
												...article,
												netPrice: article?.prices?.totalNet,
												pvp: article?.quantity * article?.prices?.unitGross,
												unitPvp: article?.prices.unitGross,
												...(!!article?.parts.length && {
													parts: article.parts.reduce((acc, part) => {
														return [
															...acc,
															{
																...part,
																netPrice: part?.prices?.totalNet,
																pvp: part?.quantity * part?.prices?.unitGross,
																unitPvp: part?.prices.unitGross
															}
														]
													}, [])
												})
											}
										]
									}, [])
								}
							]
						}, [])
					}
				]
			} else {
				return [
					...acc,
					{
						...(Toggles.isEnabled('projectRequestBudgetV2') && { articleCode: product.articleCode }),
						referenceCode: product.referenceCode || 0,
						familyId: product.familyId,
						subFamily: product.subFamily,
						applicationLocation: product.applicationLocation,
						model: product.model,
						hasAttributes: product.hasAttributes,
						attributes: product.attributes,
						quantity: product.quantity,
						lumensLed: product.lumensLed,
						watts: product.watts,
						configuratorType: product.configuratorType,
						isPersonalized: product.isPersonalized,
						type: product.type,
						hasOrderReq: product.hasOrderReq,
						discount: product.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount
							? product.quantity * product?.prices.unitGross * (1 - product.discount / 100)
							: product.quantity * product?.prices.unitGross,
						discountValue: product.discount ? product?.prices.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices.unitGross
					}
				]
			}
		}, [])
	}
	let url = `${getServiceURL('apiv2')}/budget`
	return authClient.post(url, body)
}

const createBudgetUsa = ({ budgetDetails, formData, isFinalQuote = false }) => {
	const body = {
		projectId: budgetDetails.id,
		projectName: budgetDetails.name,
		budgetNumber: formData.budgetNumber,
		budgetAlias: formData.budgetAlias,
		specifier: {
			email: formData.specEmail,
			companyName: formData.specCompanyName
		},
		...(isFinalQuote && {
			purchaserEmail: formData.purchaserEmail,
			country: {
				geonameId: formData.country.geonameId,
				name: formData.country.name
			},
			state: {
				geonameId: formData.state.geonameId,
				name: formData.state.name
			},
			city: {
				geonameId: formData.city.geonameId,
				name: formData.city.name
			}
		}),
		createdBy: formData.createdBy,
		creationDate: formatYYMMDD(formData.creationDate),
		currency: budgetDetails.currency,
		discount: budgetDetails.discount || 0,
		surcharge: budgetDetails.surcharge || 0,
		iva: budgetDetails.iva || 0,
		discountValue: budgetDetails.discountValue || 0,
		surchargeValue: budgetDetails.surchargeValue || 0,
		ivaValue: budgetDetails.ivaValue || 0,
		subTotalNet: budgetDetails.subTotal || 0,
		totalNet: budgetDetails.netTotal || 0,
		comments: budgetDetails.comment || '',
		products: Object.values(budgetDetails.products.byId).reduce((acc, product) => {
			if (product.quantity <= 0) {
				return acc
			}

			if (product?.type === productConstants.PRODUCT_TYPE_CREATION) {
				return [
					...acc,
					{
						...product,
						discount: product?.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount
							? product.quantity * product?.prices?.unitGross * (1 - product.discount / 100)
							: product.quantity * product?.prices?.unitGross,
						discountValue: product.discount ? product?.prices?.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices?.unitGross,
						compositions: product.compositions.reduce((acc, composition) => {
							const unitNetPrice = product?.discount
								? composition?.prices?.unitGross * (1 - product.discount / 100)
								: composition?.prices?.unitGross;
							const totalNetPrice = product?.discount
								? composition?.prices?.totalGross * (1 - product.discount / 100)
								: composition?.prices?.totalGross;

							return [
								...acc,
								{
									...composition,
									pvp: composition?.prices?.unitGross,
									netPrice: unitNetPrice,
									prices: {
										...composition.prices,
										unitDiscount: product?.discount || 0,
										totalDiscount: product?.discount || 0,
										unitNet: unitNetPrice,
										totalNet: totalNetPrice,
									},
									...(composition.accessories && {
										accessories: composition.accessories.reduce((acc, accessory) => {
											return [
												...acc,
												{
													...accessory,
													netPrice: accessory?.prices?.totalNet,
													pvp: accessory?.quantity * accessory?.prices?.unitGross,
													unitPvp: accessory?.prices?.unitGross
												}
											]
										}, [])
									}),
									articles: composition.articles.reduce((acc, article) => {
										return [
											...acc,
											{
												...article,
												netPrice: article?.prices?.totalNet,
												pvp: article?.quantity * article?.prices?.unitGross,
												unitPvp: article?.prices?.unitGross,
												...(!!article?.parts.length && {
													parts: article.parts.reduce((acc, part) => {
														return [
															...acc,
															{
																...part,
																netPrice: part?.prices?.totalNet,
																pvp: part?.quantity * part?.prices?.unitGross,
																unitPvp: part?.prices?.unitGross
															}
														]
													}, [])
												})
											}
										]
									}, [])
								}
							]
						}, [])
					}
				]
			} else {
				return [
					...acc,
					{
						referenceCode: product.referenceCode || 0,
						familyId: product.familyId,
						subFamily: product.subFamily,
						applicationLocation: product.applicationLocation,
						model: product.model,
						hasAttributes: product.hasAttributes,
						attributes: product.attributes,
						quantity: product.quantity,
						lumensLed: product.lumensLed,
						watts: product.watts,
						configuratorType: product.configuratorType,
						isPersonalized: product.isPersonalized,
						type: product.type,
						hasOrderReq: product.hasOrderReq,
						discount: product.discount || 0,
						pvp: product.quantity * product?.prices.unitGross,
						netPrice: product.discount ?
							product.quantity * product?.prices.unitGross * (1 - product.discount / 100)
							:
							product.quantity * product?.prices.unitGross,
						discountValue: product.discount ? product?.prices.unitGross * (product.discount / 100) : 0,
						unitPvp: product?.prices.unitGross
					}
				]
			}
		}, [])
	}

	let url = `${getServiceURL('apiv2')}/budget`
	return authClient.post(url, body)
}

const getPreviousBudgets = ({ projectId, lang }) => {
	return authClient.get(
		`${getServiceURL('projects')}/v1/budget/project/${projectId}`,
		{
			params: {
				lang
			}
		}
	)
}

const getProjectRegistration = ({ projectId, lang }) => {
	return authClient.get(
		`${getServiceURL('support')}/v1/support-request/project-registration/${projectId}`,
		{
			params: {
				lang
			}
		}
	)
}
const getBudgetRequestInfo = ({ projectId, lang, catalogId }) => {
	return authClient.get(
		`${getServiceURL('support')}/v1/support-request/info-project-budget/${projectId}`,
		{
			params: {
				lang,
				catalogId
			}
		}
	)
}

const cloneProduct = async ({ projectId, productId }) => {
	if (Toggles.isEnabled('optimizeProjectArticlesEndpoints')) {
		return authClient.post(`${getServiceURLserverSide('v3')}/projects/${projectId}/products/${productId}/clone`)
	} else {
		return authClient.post(
			`${getServiceURL('apiv2')}/projects/${projectId}/products/${productId}/clone`
		)
	}
}

const deleteProduct = ({ projectId, productId }) => {
	if (Toggles.isEnabled('optimizeProjectArticlesEndpoints')) {
		return authClient.delete(`${getServiceURLserverSide('v3')}/projects/${projectId}/products/${productId}`)
	} else {
		return authClient.delete(
			`${getServiceURL('projects')}/v1/projects/${projectId}/products/${productId}`
		)
	}

}

const updateProduct = ({ projectId, productId, attributesToUpdate, userEmail }) => {
	if (TogglesV2.isEnabledForEmail("enableSceneMode", userEmail)) {
		return authClient.put(
			`${getServiceURLserverSide('v3')}/project/${projectId}/product/${productId}`,
			attributesToUpdate
		)
	} else {
		return authClient.put(
			`${getServiceURL('projects')}/v1/projects/${projectId}/products/${productId}`,
			attributesToUpdate
		)
	}
}

const setProductName = ({ projectId, productId, name }) => {
	return authClient.patch(
		`${getServiceURLserverSide('v3')}/projects/${projectId}/products/${productId}/name`,
		{ name }
	)
}

const setCompositionName = ({ projectId, productId, compositionId, name }) => {
	return authClient.patch(
		`${getServiceURLserverSide('v3')}/projects/${projectId}/products/${productId}/compositions/${compositionId}/name`,
		{ name }
	)
}

const updateProject = ({ projectId, fieldsToUpdate }) => {
	return authClient.put(
		`${getServiceURL('projects')}/v1/projects/${projectId}`,
		fieldsToUpdate
	)
}

const updateProjectv2 = (body) => {
	return authClient.post(`${getServiceURL('apiv2')}/project`, body)
}

const deleteProject = (projectId) => {
	return authClient.delete(`${getServiceURL('projects')}/v1/projects/${projectId}`)
}

const getTooltipInfo = ({ projectId }) => {
	return authClient.get(
		`${getServiceURL('apiv2')}/support/tooltip-info/${projectId}`
	)
}


const createRequestBudget = ({ lang, files, siteLocation, catalogId, description, project }) => {
	let url = Toggles.isEnabled('projectRequestBudgetV2') ? `${getServiceURL('apiv2')}/support-request` : `${getServiceURL('support')}/v1/support-request/project-budget`

	const SUPPORT_TYPE_ID = 2
	const SUPPORT_TYPE_DESCRIPTION = 'project-budget'

	return authClient.post(url, {
		lang,
		files,
		siteLocation,
		catalogId,
		description,
		project,
		supportType: {
			id: SUPPORT_TYPE_ID,
			description: SUPPORT_TYPE_DESCRIPTION,
		},
		_supportType: SUPPORT_TYPE_DESCRIPTION
	})
}

const createProjectRegistration = ({ registrationData }) => {
	return authClient.post(
		`${getServiceURL('support')}/v1/support-request/project-registration-usa`,
		registrationData
	)
}

const assignProjectToThirdParty = ({ projectId, email }) => {
	const url = `${getServiceURL('apiv2')}/${projectId}/assign-thirdparty`
	return authClient.post(
		url,
		{
			email
		}
	)
}

const getProjectTechInfo = (projectId) => {
	return authClient.get(
		`${getServiceURL('projects')}/v1/projects/${projectId}/tech-info-actions`
	)
}

const downloadProjectFiles = (docs, lang, catalogId) => {
	return authClient.post(
		`${getServiceURL('documents')}/v1/docs-web`,
		{
			docs
		},
		{
			params: {
				lang,
				catalogId
			}
		}
	)
}

const validateProduct = ({ projectId, productId }) => {
	return authClient.put(`${getServiceURL('apiv2')}/project/${projectId}/product/${productId}/validate`)
}

const getPDFnewFlow = ({ creationCode, lang }) => {
	return authClient.get(`${getServiceURL('apiv2')}/documents/generate-creation-specsheet/${creationCode}?lang=${lang}`)
}

const updateProjectName = (projectId, projectName) => {
	return authClient.put(`${getServiceURL('apiv2')}/project/${projectId}/projectName`, {
		projectName: projectName
	})
}
const getProjectName = (projectId) => {
	return authClient.get(`${getServiceURL('apiv2')}/project/${projectId}/projectName`)
}

const getOrdersResume = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/projects/${projectId}/orders`)
}

const getAllOrdersByProjectId = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/projects/${projectId}/allOrders`)
}

const forkProject = (projectId) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/project/${projectId}/fork`)
}

const discardProject = (projectId) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/project/${projectId}/discard`)
}

const shareProjectWithEmail = (projectId, email) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/project/${projectId}/shareWith`, { email })
}

const updateOpportunityInstallationDate = ({ projectId, installationDate }) => {
	return authClient.put(`${getServiceURLserverSide('v3')}/project/${projectId}/installation_date`, { scheduledInstallationDate: installationDate })
}

const getOpportunityInstallationDate = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/project/${projectId}/installation_date`)
}

const getProjectScenes = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/projects/${projectId}/scenes`)
}

const createProjectScene = (projectId, { name, description, order }) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/projects/${projectId}/scenes`, { name, description, order })
}

const deleteProjectScene = (sceneId) => {
	return authClient.delete(`${getServiceURLserverSide('v3')}/scenes/${sceneId}`)
}

const copyProjectScene = (sceneId) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/scenes/${sceneId}/copy`)
}

const updateProjectScene = (sceneId, body) => {
	return authClient.patch(`${getServiceURLserverSide('v3')}/scenes/${sceneId}`, body)
}

const getProjectCanOrder = (projectId, { cache = true } = {}) => {
	const params = cache ? {} : { cache: 'false' }
	return authClient.get(`${getServiceURLserverSide('v3')}/project/${projectId}/can_order`, { params })
}

const getProjectItemLights = (projectId) => {
	return authClient.get(`${getServiceURLserverSide('v3')}/projects/${projectId}/item-lights`)
}

const generateProjectItemLights = (projectId) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/projects/${projectId}/item-lights/generate`)
}

const downloadProjectScenes = (projectId) => {
	return authClient.post(`${getServiceURLserverSide('v3')}/project/${projectId}/scenes/download`, {}, { responseType: 'blob' })
}

export default {
	addProduct,
	addProductByRef,
	addProducts,
	addProductsV2,
	addProductsByRef,
	assignProjectToThirdParty,
	cloneProduct,
	createBudget,
	createBudgetUsa,
	createProject,
	createProjectv2,
	upsertVersionV2,
	setProjectNameV2,
	createProjectWithReferences,
	createProjectRegistration,
	createRequestBudget,
	deleteProduct,
	deleteProject,
	duplicateProject,
	versionProject,
	getProjectVersions,
	getPreviousBudgets,
	getProject,
	getProjectv2,
	getProjectDetail,
	getCrmOnlyProducts,
	getProjectToBudgetV2,
	getProjectRegistration,
	getBudgetRequestInfo,
	getProjectTechInfo,
	getTooltipInfo,
	setProductName,
	setCompositionName,
	updateProduct,
	updateProject,
	updateProjectv2,
	downloadProjectFiles,
	validateProduct,
	getPDFnewFlow,
	updateProjectName,
	getProjectName,
	getOrdersResume,
	getAllOrdersByProjectId,
	getProjectV3,
	forkProject,
	discardProject,
	shareProjectWithEmail,
	updateOpportunityInstallationDate,
	getOpportunityInstallationDate,
	getProjectScenes,
	createProjectScene,
	deleteProjectScene,
	copyProjectScene,
	updateProjectScene,
	getProjectItemLights,
	generateProjectItemLights,
	downloadProjectScenes,
	getProjectCanOrder
}
